import type { InvitedMember, ShortLinkResponse } from '@app-types/scene';
import { http } from '@utils/http';
import { createLogger } from '@utils/logger';

const log = createLogger('share-service');

// ---------------------------------------------------------------------------
// 短链
// ---------------------------------------------------------------------------

/** 会话级缓存：sceneId → shortUrl。 */
const shortLinkCache = new Map<string, string>();

/**
 * 为场景生成短链。会话内同一 sceneId 仅请求一次。
 * 短链服务接口未就绪时降级返回当前 URL。
 */
export async function createShortLink(sceneId: string): Promise<ShortLinkResponse> {
  const cached = shortLinkCache.get(sceneId);
  if (cached) return { shortUrl: cached };

  try {
    const { data } = await http.post<ShortLinkResponse>('/api/v1/short-link', {
      scene_id: sceneId,
    });
    shortLinkCache.set(sceneId, data.shortUrl);
    return data;
  } catch (err) {
    // 降级：短链接口失败返回完整 URL。
    log.warn('createShortLink failed, falling back to full URL', err);
    return { shortUrl: window.location.href };
  }
}

// ---------------------------------------------------------------------------
// 受邀者管理
// ---------------------------------------------------------------------------

/** 获取场景邀请列表（仅创作者可调）。 */
export async function listInvitedMembers(sceneId: string): Promise<InvitedMember[]> {
  const { data } = await http.get<InvitedMember[]>(`/api/v1/scenes/${sceneId}/invites`);
  return data;
}

/** 邀请成员。 */
export async function inviteMember(sceneId: string, email: string): Promise<void> {
  await http.post(`/api/v1/scenes/${sceneId}/invites`, { email });
}

/** 移除邀请。 */
export async function removeInvitedMember(sceneId: string, memberId: string): Promise<void> {
  await http.delete(`/api/v1/scenes/${sceneId}/invites/${memberId}`);
}

// ---------------------------------------------------------------------------
// 发布状态
// ---------------------------------------------------------------------------

/** 更新场景发布状态（仅创作者可调）。 */
export async function updatePublishStatus(
  sceneId: string,
  status: 'published' | 'unpublished',
): Promise<void> {
  await http.put(`/api/v1/scenes/${sceneId}/publish`, { status });
  log.info('updatePublishStatus', { sceneId, status });
}
